/* eslint-disable */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";

import Utils from "../../Utils";
import {
	toEnglishAmountValue,
	toEnglishIncomeValue,
	toEnglishLegalSourceValue,
	toEnglishSectorValue,
} from "../../Assets/OptionsTranslations";

import { IUseLegalInvestorForm } from "./IUseLegalInvestorForm";
import { emptyInvestor, emptyInvestorErrors } from "../../Assets/Data/EmptyValues";
import { DefaultValue, InvestorDTO, InvestorErrorsDTO } from "../../Models";

export const useLegalInvestorForm = ({
	translations,
	investorAPI,
	contractAPI,
	unlockNextStep,
	getPlacePredictions,
	placesService,
	hideCommercialRegister = false,
}: IUseLegalInvestorForm) => {
	const [searchParams, _] = useSearchParams();

	const [loading, setLoading] = useState<boolean>(false);
	const [noContent, setNoContent] = useState<boolean>(false);
	const [activityInList, setActivityInList] = useState<boolean>(true);
	const [sourceInList, setSourceInList] = useState<boolean>(true);
	const [nationalitiesList, setNationalitiesList] = useState<any[]>([]);
	const [contractLanguage, setContractLanguage] = useState<string>('es');
	const [contractId, setContractId] = useState<number>(0);
	const [addressInputs, setAddressInputs] = useState<string[]>([]);
	const [manualAddressVisibility, setManualAddressVisibility] = useState({
		socialAddress: false,
		taxResidence: false,
	});

	const [activeAddress, setActiveAddress] = useState<{ index: number, field: string } | null>(null);

	const [investorData, setInvestorData] = useState<InvestorDTO>(emptyInvestor);

	const [errors, setErrors] = useState<InvestorErrorsDTO>(emptyInvestorErrors);

	const [defaultValues, setDefaultValues] = useState<DefaultValue[]>([]);

	const updateManualAddressVisibility = (
		field: 'socialAddress' | 'taxResidence',
		visible: boolean,
	) => {
		setManualAddressVisibility((prevState) => ({
			...prevState,
			[field]: visible,
		}));
	};

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			configureInitialData(Number(searchParams.get('ContractId')));
		}
		else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = async(hash: string) => {
				return await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					} 
				})
			}
			
			getContractIdByHash(hash!);
		}
		getNationalities();
	}, [translations]);

	const configureInitialData = (ContractId: number) => {
		setLoading(true);
		setContractId(ContractId);
		investorAPI.getDefaultValuesInvestor(ContractId)
			.then((resp: any) => {
				if (resp.success && resp.data) {
					const newDefaultValues: DefaultValue[] = resp.data;
					setDefaultValues(newDefaultValues);
					investorAPI.getInvestors(ContractId).then(res => {
						if(res.success){
							if (res.data[0].socialAddress) {
								setAddressInputs((prevState: string[]) => {
									const updatedInputs = [...prevState];
									updatedInputs[0] = `${res.data[0].socialAddress}, ${res.data[0].socialAddressPostalCode}, ${res.data[0].socialAddressCity}, ${res.data[0].socialAddressCountry}`;
									return updatedInputs;
								});
							}
							if (res.data[0].taxResidence) {
								setAddressInputs((prevState: string[]) => {
									const updatedInputs = [...prevState];
									updatedInputs[1] = `${res.data[0].taxResidence}, ${res.data[0].taxResidencePostalCode}, ${res.data[0].taxResidenceCity}, ${res.data[0].countryTaxResidence}`;
									return updatedInputs;
								});
							}
							if ((res.data[0].socialAddress === res.data[0].taxResidence)
								&& (res.data[0].socialAddressCity === res.data[0].taxResidenceCity)
								&& (res.data[0].socialAddressPostalCode === res.data[0].taxResidencePostalCode)
								&& (res.data[0].socialAddressCountry === res.data[0].countryTaxResidence)) {
								res.data[0].sameAddresses = true;
							} else {
								res.data[0].sameAddresses = false;
							}

							newDefaultValues.forEach((defVal: DefaultValue) => {
								if (res.data[0].hasOwnProperty(defVal.field) && defVal.value && res.data[0][defVal.field] === "") {
									(res.data[0] as any)[defVal.field] = defVal.value;
								}
							});

							res.data[0].sector = toEnglishSectorValue(res.data[0].sector);
							res.data[0].source = toEnglishLegalSourceValue(res.data[0].source);
							res.data[0].incomes = toEnglishIncomeValue(res.data[0].incomes);
							res.data[0].lastPatrimony = toEnglishAmountValue(res.data[0].lastPatrimony);

							if(res.data.length > 0 ) setInvestorData(res.data[0]);
						} else {
							setNoContent(true);
						}
						setLoading(false);
					})
					.catch(err => {
						console.log(err);
					});
				}
			});
		contractAPI.getContract(ContractId).then(res => {
			if(res.success){
				if(res.data?.idLanguage){
					setContractLanguage(res.data.idLanguage === 1 ? 'es' : 'en');
				}
			}
		})
	};

	const getNationalities = () => {
		investorAPI.getCountries().then(res => {
			if (res !== "") {
				setNationalitiesList(res);
			}
		})
		.catch(err => {
			console.log(err);
		});
	};

	const handleChangeData = (e: any) => {
		const { name, value } = e.target;
		setErrors((prevState: any) => ({
			...prevState,
			[name]: false
		}));
		setInvestorData((prevState: any) => ({
			...prevState,
			[name]: value
		}));
	};

	const handleSelectData = (field: string) => (value: string) => {
		field === 'sector' && setActivityInList(value !== 'Otro');
		field === 'source' && setSourceInList(value !== 'Otro');
		setErrors((prevState: any) => ({
			...prevState,
			[field]: false
		}));
		setInvestorData((prevState: InvestorDTO) => {
			const updatedInvestor: InvestorDTO = {...prevState};
			updatedInvestor[field] = value;
			return updatedInvestor;
		});
	};

	const saveData = () => {
		let obj = {...investorData}
		setLoading(true);
		if (validateData(true)) {
			obj.sector = toEnglishSectorValue(obj.sector);
			obj.source = toEnglishLegalSourceValue(obj.source);
			obj.incomes = toEnglishIncomeValue(obj.incomes);
			obj.lastPatrimony = toEnglishAmountValue(obj.lastPatrimony);
			obj.sector = obj.sector === 'Otro' ? obj.otherSector : obj.sector;
			if (obj.sameAddresses) {
				obj.taxResidence = obj.socialAddress;
				obj.taxResidenceCity = obj.socialAddressCity;
				obj.taxResidencePostalCode = obj.socialAddressPostalCode;
				obj.countryTaxResidence = obj.socialAddressCountry;
				obj.taxResidenceExtra = obj.socialAddressExtra;
			}
			investorAPI.updateInvestors([obj]).then((res: any) => {
				if(res.success){
					message.success(translations.datosGuardados);
					unlockNextStep(contractId);
				}else{
					message.error(translations.messageError);
				}
				setLoading(false);
			});
		} else {
			setLoading(false);
		}
	};

	const validateData = (modifyErrors: boolean) => {
		const legalFormRegex = /^[A-Za-z0-9À-ÖØ-öø-ÿ'-.]*(?:\s[A-Za-z0-9À-ÖØ-öø-ÿ'-.]*)*$/;
		let isOK = true;
		if (!investorData.name) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, name: true }));
			isOK = false;
		}
		if (!investorData.legalForm) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, legalForm: true }));
			isOK = false;
		} else if (!legalFormRegex.test(investorData.legalForm)) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, legalFormFormat: true }));
			isOK = false;
		}

		if (!investorData.entityType) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, entityType: true }));
			isOK = false;
		}
		if (!investorData.constitutionDate) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, constitutionDate: true }));
			isOK = false;
		}
		if (!investorData.constitutionPlace) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, constitutionPlace: true }));
			isOK = false;
		}
		if (!investorData.jurisdiction) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, jurisdiction: true }));
			isOK = false;
		}
		if (!investorData.socialAddress || !investorData.socialAddressCity || !investorData.socialAddressPostalCode || !investorData.socialAddressCountry) {
			modifyErrors && setErrors((prevState: any) => ({
				...prevState,
				socialAddress: true,
				socialAddressCity: !investorData.socialAddressCity,
				socialAddressPostalCode: !investorData.socialAddressPostalCode,
				socialAddressCountry: !investorData.socialAddressCountry,
			}));
			modifyErrors && updateManualAddressVisibility('socialAddress', true);
			isOK = false;
		} else {
			modifyErrors && updateManualAddressVisibility('socialAddress', false);
		}
		if (!hideCommercialRegister && !investorData.commercialRegister) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, commercialRegister: true }));
			isOK = false;
		}
		if (!investorData.entityNationality) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, entityNationality: true }));
			isOK = false;
		}
		if (!investorData.cif) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, cif: true }));
			isOK = false;
		}
		if (!investorData.sameAddresses && (!investorData.taxResidence || !investorData.taxResidenceCity || !investorData.taxResidencePostalCode || !investorData.countryTaxResidence)) {
			modifyErrors && setErrors((prevState: any) => ({
				...prevState,
				taxResidence: true,
				taxResidenceCity: !investorData.taxResidenceCity,
				taxResidencePostalCode: !investorData.taxResidencePostalCode,
				countryTaxResidence: !investorData.countryTaxResidence,
			}));
			modifyErrors && updateManualAddressVisibility('taxResidence', true);
			isOK = false;
		} else {
			modifyErrors && updateManualAddressVisibility('taxResidence', false);
		}
		if (!investorData.operationCountries) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, operationCountries: true }));
			isOK = false;
		}
		if (!investorData.bankDeposits) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, bankDeposits: true }));
			isOK = false;
		}
		if (!investorData.bankAddress) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, bankAddress: true }));
			isOK = false;
		}
		if (!investorData.iban) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, iban: true }));
			isOK = false;
		}
		if (!investorData.biC_SWIFT) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, biC_SWIFT: true }));
			isOK = false;
		}
		if (investorData.organism && !investorData.regulationOrganism) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, regulationOrganism: true }));
			isOK = false;
		}
		if (investorData.organism && !investorData.countryRegulator) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, countryRegulator: true }));
			isOK = false;
		}
		if (investorData.organism && !investorData.webRegulator) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, webRegulator: true }));
			isOK = false;
		}
		if (investorData.organism && !investorData.regulatoryRecord) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, regulatoryRecord: true }));
			isOK = false;
		}
		if (investorData.contributor && !investorData.marketName) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, marketName: true }));
			isOK = false;
		}
		if (investorData.contributor&& !investorData.marketCountry) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, marketCountry: true }));
			isOK = false;
		}
		if (investorData.contributor && !investorData.marketWeb) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, marketWeb: true }));
			isOK = false;
		}
		if (investorData.contributor && !investorData.marketRegistry) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, marketRegistry: true }));
			isOK = false;
		}
		if ((investorData.sector === 'Otro' && !investorData.otherSector) || !investorData.sector) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, sector: true }));
			isOK = false;
		}
		if (!investorData.incomes) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, incomes: true }));
			isOK = false;
		}
		if (!investorData.source) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, source: true }));
			isOK = false;
		}
		if (!investorData.lastPatrimony) {
			modifyErrors && setErrors((prevState: any) => ({ ...prevState, lastPatrimony: true }));
			isOK = false;
		}

		return isOK;
	};

	const handleCheckedData = (e: any) => {
		const { name } = e.target;
		setInvestorData((prevState: any) => ({
			...prevState,
			[name]: e.target.checked
		}));
	};

	const onChangeSameAddresses = (checked: boolean) => {
		setInvestorData((prevState: InvestorDTO) => {
			const updatedInvestor = { ...prevState, sameAddresses: checked};
			return updatedInvestor;
		});
		if (checked) {
			updateManualAddressVisibility('taxResidence', false);
		}
	};

	const getAddressComponent = (placeDetails: any, type: string) => {
		const component = placeDetails?.address_components?.find((component: any) => component.types.includes(type));
		return component ? component.long_name : "";
	};

	const getFormattedStreetAddress = (placeDetails: any, fallbackValue: string) => {
		const route = getAddressComponent(placeDetails, "route");
		const streetNumber = getAddressComponent(placeDetails, "street_number");
		const premise = getAddressComponent(placeDetails, "premise");
		const subpremise = getAddressComponent(placeDetails, "subpremise");
		const pointOfInterest = getAddressComponent(placeDetails, "point_of_interest");
		const neighborhood = getAddressComponent(placeDetails, "neighborhood");
		const placeName = placeDetails?.name?.trim?.() || "";
		const fallbackStreet = fallbackValue
			.split(",")[0]
			.split(" - ")[0]
			.trim();

		return [route, streetNumber].filter(Boolean).join(", ")
			|| premise
			|| subpremise
			|| placeName
			|| pointOfInterest
			|| neighborhood
			|| fallbackStreet;
	};

	const handleSelectAddress = (field: string) => (value: string, option: any) => {
		if (!activeAddress) return;
		if (field === 'taxResidence') {
			setAddressInputs(prevState => {
				const updatedInputs = [...prevState];
				updatedInputs[1] = value;
				return updatedInputs;
			});
			placesService?.getDetails(
				{
					placeId: option.key,
				},
				(placeDetails: any) => {
					const address = getFormattedStreetAddress(placeDetails, value);
					const city = getAddressComponent(placeDetails, "locality") || getAddressComponent(placeDetails, "administrative_area_level_2");
					const postalCode = getAddressComponent(placeDetails, "postal_code");
					const country = getAddressComponent(placeDetails, "country");

					setInvestorData((prevState: InvestorDTO) => {
						const updatedInvestor = { ...prevState };
						updatedInvestor.taxResidence = address;
						updatedInvestor.taxResidenceCity = city;
						updatedInvestor.taxResidencePostalCode = postalCode;
						updatedInvestor.countryTaxResidence = country;
						return updatedInvestor;
					});
					setErrors((prevState: any) => ({
						...prevState,
						taxResidence: !address || !city || !postalCode || !country,
						taxResidenceCity: !city,
						taxResidencePostalCode: !postalCode,
						countryTaxResidence: !country,
					}));
					updateManualAddressVisibility('taxResidence', !address || !city || !postalCode || !country);
				}
			);
		} else {
			setAddressInputs(prevState => {
				const updatedInputs = [...prevState];
				updatedInputs[0] = value;
				return updatedInputs;
			});
			placesService?.getDetails(
				{
					placeId: option.key,
				},
				(placeDetails: any) => {
					const address = getFormattedStreetAddress(placeDetails, value);
					const city = getAddressComponent(placeDetails, "locality") || getAddressComponent(placeDetails, "administrative_area_level_2");
					const postalCode = getAddressComponent(placeDetails, "postal_code");
					const country = getAddressComponent(placeDetails, "country");

					setInvestorData((prevState: InvestorDTO) => {
						const updatedInvestor = { ...prevState };
						updatedInvestor.socialAddress = address;
						updatedInvestor.socialAddressCity = city;
						updatedInvestor.socialAddressPostalCode = postalCode;
						updatedInvestor.socialAddressCountry = country;
						return updatedInvestor;
					});
					setErrors((prevState: any) => ({
						...prevState,
						socialAddress: !address || !city || !postalCode || !country,
						socialAddressCity: !city,
						socialAddressPostalCode: !postalCode,
						socialAddressCountry: !country,
					}));
					updateManualAddressVisibility('socialAddress', !address || !city || !postalCode || !country);
				}
			);
		}
	};

	const handleSearchAddress = (field: string) => (value: string) => {
		if (field === 'taxResidence') {
			setActiveAddress({ index: 1, field });
			getPlacePredictions({ input: value });
		} else {
			setActiveAddress({ index: 0, field });
			getPlacePredictions({ input: value });
		}
	};

	const onChangeNotificationsEnabled = (checked: boolean) => {
		setInvestorData((prevState: InvestorDTO) => {
			const updatedInvestor = { ...prevState, notificationsEnabled: checked};
			return updatedInvestor;
		});
	};

	return {
		loading,
		noContent,
		errors,
		investorData,
		nationalitiesList,
		activityInList,
		sourceInList,
		contractId,
		addressInputs,
		manualAddressVisibility,
		defaultValues,
		handleChangeData,
		handleSelectData,
		handleCheckedData,
		saveData,
		validateData,
		onChangeSameAddresses,
		handleSearchAddress,
		handleSelectAddress,
		onChangeNotificationsEnabled,
		contractLanguage
	};
};

